// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   POST https://api.orioralabs.com/api/apps/oriora_garage/<action>
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Vehicle data stays local; only a small
// DERIVED-SIGNALS object (economy averages, cost-per-distance, service intervals,
// spend split, mileage projection — see signals.rs) is sent. Raw notes / exact plate
// never leave the device.
//
// TWO billable actions, each ONE call (CRUD, not agentic — text-in / text-out):
//   • narrate — the monthly/on-demand narration of the computed figures
//   • ask     — "Ask your garage" Q&A over the same signal slice (+ the question)
//
// HARDEST guardrail (enforced server-side, mirrored in our UI copy): the output
// DESCRIBES the computed numbers only — it never advises, never says a service is due
// or needed, never diagnoses, never gives financial advice. We compute the signals,
// call once, render the result. No second call, no tool loop, no vendor SDK in-app.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { GarageSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_garage";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large" | "refused";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ── THE FENCE (pre-LLM) ────────────────────────────────────────────────────────
// The observational ceiling (AI-VALUE.md §"The refusal / compliance fence", DESIGN.md "THE
// OBSERVATIONAL FENCE") was PROMPT-ONLY before this pass — the server prompt declined, but
// nothing stopped an advice-seeking question from reaching the billed call in the first place
// (workout_tracker precedent: "prompting alone does not hold"). Deterministic refusal here,
// BEFORE any billed call, on BOTH free-text entries ("Ask your garage" card → askGarage below,
// and chat → chat/brain.ts::askBrain). Patterns are INTENT-PHRASED asking shapes, never bare
// tokens — the user's own log stays free: "add service: oil change", "when did I last change
// the oil?" (elapsed FACT), "how much did I spend on repairs?" (their own spend) all pass;
// "when is my service DUE?" (a verdict), "is it safe to drive?", "diagnose this noise",
// "is $400 a fair price?" are refused. Built + verified by fence-battery.mjs (2026-08-26).
export function isCarAdviceQuestion(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  const PATTERNS: RegExp[] = [
    // what-should-I-do prescription (repair/replace/buy/sell/parts/oil — all "should I" shapes)
    /\bshould i\b/,
    /\bdo i need\b.{0,30}\b(tires?|tyres?|brakes?|pads?|oil|parts?|battery|filters?|fluids?|service|repairs?|replac\w+|chang\w+)\b/,
    /\b(recommend|suggest|advise) (a|an|me|some)\b/,
    // "due"/"overdue" VERDICTS — the app reports logged intervals as elapsed facts, never a
    // verdict ("when DID I last service" passes; "when IS my service DUE" is refused).
    /\b(when|what).{0,30}\b(due|overdue)\b/,
    /\bam i (due|overdue)\b/,
    /\bis (my|the) (\w+ ){0,3}(due|overdue)\b/,
    /\bhow long (until|till|before)\b.{0,30}\b(service|oil|change|due|replac\w+)\b/, // "since" stays free — that's the elapsed fact
    // safety-to-drive
    /\bis (it|this|my car|the car) (safe|ok|okay|fine) to (drive|keep driving)\b/,
    /\b(safe|dangerous|unsafe) to (drive|keep driving)\b/,
    /\bcan i (still |safely )?(drive|keep driving)\b/,
    // diagnosis — a symptom word beside an asking shape (a symptom word in a LOG note passes)
    /\bwhat('?s| is) (that|this|the)\b.{0,30}\b(noise|sound|rattl\w+|squea\w+|grind\w+|knock\w+|clunk\w*|smell|smoke|leak\w*|vibrat\w+)\b/,
    /\bwhy (is|does|do) (my|the)\b.{0,30}\b(nois\w+|sound\w*|shak\w+|smok\w+|leak\w+|stall\w+|squea\w+|grind\w+|vibrat\w+|rattl\w+|smell\w+|pull\w+|overheat\w+)\b/,
    /\bwhat('?s| is) wrong\b/,
    /\bdiagnos/,
    /\bis (this|that|it) (\w+ )?(normal|serious|bad|dangerous)\b/,
    // financial judgment — prices judged, never the user's own spend record
    /\b(fair|reasonable|good) (price|quote|deal)\b/,
    /\b(price|quote|cost|estimate)\b.{0,25}\b(fair|reasonable|too (much|high|expensive)|rip ?off)\b/,
    /\bovercharg/,
    /\bworth (repairing|fixing|replacing|keeping)\b/,
  ];
  return PATTERNS.some((re) => re.test(q));
}

export const CAR_ADVICE_DECLINED =
  "Garage describes your own logged records — fill-ups, services, and what you spent — and how long " +
  "or far it's been since each service. It can't say whether something is due, safe, or worth it, " +
  "diagnose a symptom, or judge a price — that needs a qualified mechanic. Try a fact question " +
  "instead, like \"when did I last change the oil?\"";

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(action: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${action}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface GarageNarration {
  narration: string;
}
export interface GarageAnswer {
  answer: string;
}

/**
 * Narrate the vehicle's computed figures (the monthly/on-demand call). The ONE call
 * per action. We send ONLY the derived signals (economy/cost/service aggregates), never
 * the raw fill-up rows or notes. The server prompt reads `signals` and produces a short,
 * OBSERVATIONAL narration that names the specific numbers ("cost-per-mile rose 18% since
 * the last service") — never advice, never "you should service X", never diagnosis.
 *
 * POST /api/apps/oriora_garage/narrate  (operator task: server prompt reads `signals`).
 */
export async function narrateGarage(signals: GarageSignals): Promise<GarageNarration> {
  if (signals.overall.fillups === 0 && signals.overall.services === 0) {
    throw new BrainError("unavailable", "Log a fill-up or service first — there's nothing to narrate yet.");
  }
  const r = await brainCall<Partial<GarageNarration>>("narrate", { signals });
  return { narration: String(r.narration ?? "").trim() };
}

/**
 * "Ask your garage" — a repeat billable Q&A over the SAME derived signal slice plus the
 * user's question ("how much did this car cost me this year?", "when did I last replace
 * the tires?"). ONE call, text-in/text-out. The server answers from the signals only and
 * stays observational — it states facts the numbers support, and says it can't answer if
 * the data doesn't cover it; it never advises or recommends a service.
 *
 * POST /api/apps/oriora_garage/ask  (operator task: server prompt reads `signals` + `question`).
 */
export async function askGarage(signals: GarageSignals, question: string): Promise<GarageAnswer> {
  const q = question.trim();
  if (!q) throw new BrainError("unavailable", "Type a question first.");
  // THE FENCE — hard pre-LLM refusal: no billed call for advice/due/diagnosis/price-judgment asks.
  if (isCarAdviceQuestion(q)) throw new BrainError("refused", CAR_ADVICE_DECLINED);
  const r = await brainCall<Partial<GarageAnswer>>("ask", { signals, question: q });
  return { answer: String(r.answer ?? "").trim() };
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile. Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must
// allowlist `oriora-garage` as a return target (server-side, operator task).
// ── Cloud backup (the user owns their data) — fleet standard ────────────────────
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }
async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}
/** Save the local backup envelope to the user's connected cloud store (GitHub/Drive). */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ data: envelope }) });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}
/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

export const openWebLogin = () => invoke<void>("open_login_window");
